import * as React from "react";
import { useLocation } from "wouter";
import { Luggage, ArrowRight, ArrowLeft, CalendarClock } from "lucide-react";

/**
 * Events category landing — event-driven / one-off organizing that's triggered by
 * a life event with a start and an end (the home reverts to baseline afterward).
 * One of the three top-level Organize categories: Placement · Events · Keep It Up.
 *
 * Today it holds only Packing. It stands up now (rather than waiting) so the
 * "one-off occasion" concept is visible immediately and future event flows —
 * Moving, Holiday decorate / un-decorate — slot in here without reshuffling.
 */
type EventCard = {
  key: string;
  label: string;
  hint: string;
  icon: React.ElementType;
  href: string;
};

const EVENTS: EventCard[] = [
  { key: "packing", label: "Packing", hint: "Pack for a trip without losing track", icon: Luggage, href: "/organize/packing" },
];

export default function EventsPage() {
  const [, navigate] = useLocation();

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header>
        <button
          onClick={() => navigate("/organize")}
          className="inline-flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors mb-2"
        >
          <ArrowLeft className="w-4 h-4" />Organize
        </button>
        <div className="flex items-center gap-2 mb-1">
          <CalendarClock className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium text-primary">Events</span>
        </div>
        <h1 className="text-4xl font-semibold text-ink font-headline tracking-[-0.02em]">For the occasion</h1>
        <p className="text-body mt-1 text-sm">One-off organizing for a trip or event — set it up, then put things back after.</p>
      </header>

      <div className="space-y-3">
        {EVENTS.map((e) => {
          const Icon = e.icon;
          return (
            <a
              key={e.key}
              href={e.href}
              className="flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-primary/40 transition-colors group"
            >
              <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 bg-amethyst-soft">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[1.0625rem] font-semibold text-ink">{e.label}</div>
                <div className="text-[0.8125rem] text-faint">{e.hint}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
            </a>
          );
        })}
      </div>

      {/* Coming-later hint — keeps the "one-off occasion" concept honest while sparse */}
      <p className="text-[0.8125rem] text-faint px-1">
        More one-off flows are on the way — like moving house and holiday decorating.
      </p>
    </div>
  );
}
